import { Link } from 'react-router'
import { ArrowRight } from 'lucide-react'
import { artisanSlug } from '~/lib/programmatic-seo'

/**
 * Hub-and-spoke internal linking block. Renders 4 contextual outbound
 * links per city page: service category, métier guide, service × city,
 * and tarifs × city. Builds the link graph so Google crawls deep and
 * every programmatic city page also passes equity into pillar pages.
 *
 * Pass primarySlug from the page's first category. cityName + citySlug
 * drive the URL builders.
 */
export function CrossLinksHub({ primarySlug, cityName, citySlug, currentRoute }: {
  primarySlug: string
  cityName: string
  citySlug: string
  /** Self-link is skipped — pass the current page kind. */
  currentRoute?: 'services' | 'metier' | 'tarifs' | 'rge' | 'cee' | 'problemes' | 'devis-ville'
}) {
  const links: Array<{ to: string; label: string; sub: string }> = []

  if (currentRoute !== 'services') {
    links.push({
      to: `/services/${primarySlug}/${citySlug}`,
      label: `Service ${primarySlug} à ${cityName}`,
      sub: 'Trouver un pro vérifié',
    })
  }
  if (currentRoute !== 'metier') {
    links.push({
      to: `/metier/${primarySlug}`,
      label: `Métier : ${primarySlug}`,
      sub: 'Guide complet du métier',
    })
  }
  if (currentRoute !== 'tarifs') {
    links.push({
      // /tarifs/{artisan}/{city} — the 2-seg route resolves by ARTISAN slug
      // (plombier), not the service/category slug (plomberie). Passing the raw
      // primarySlug 404'd on every city page (GSC audit 2026-07). artisanSlug
      // maps plomberie→plombier so the hub link actually resolves.
      to: `/tarifs/${artisanSlug(primarySlug)}/${citySlug}`,
      label: `Tarifs ${primarySlug} ${cityName}`,
      sub: 'Prix moyens 2026',
    })
  }
  if (currentRoute !== 'devis-ville') {
    // /devis-ville 301-redirects to /prix/{artisan}-{city}. Linking the redirect
    // wasted crawl budget + fed the GSC "Page with redirect" bucket on every
    // city page — point straight at the 200 target (GSC audit 2026-07).
    links.push({
      to: `/prix/${artisanSlug(primarySlug)}-${citySlug}`,
      label: `Prix ${primarySlug} ${cityName}`,
      sub: '3 devis gratuits 48h',
    })
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
      <h2 className="text-base font-bold text-slate-900">
        Explorer {primarySlug} à {cityName}
      </h2>
      <ul className="mt-3 grid gap-2 sm:grid-cols-2">
        {links.map((l) => (
          <li key={l.to}>
            <Link
              to={l.to}
              className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 text-sm hover:border-primary hover:bg-blue-50"
            >
              <div>
                <p className="font-medium text-slate-900">{l.label}</p>
                <p className="text-xs text-slate-500">{l.sub}</p>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-slate-400" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
